'use client'

import { useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

export const faqs = [
  {
    question: "What services does Apex Visualtech provide?",
    answer: "Apex Visualtech is a full-service premium digital agency based in New Delhi, India. We specialize in full-stack web development (Next.js, React, Node.js), cinematic videography (brand films, drone footage, corporate videos), high-end graphic design (logo, brand identity, social media), and performance-driven SEO services including technical SEO and content strategy."
  },
  {
    question: "Where is Apex Visualtech located and which areas do you serve?",
    answer: "We are based in New Delhi, India, and serve clients across the entire NCR region including Noida, Gurugram, Ghaziabad, and Faridabad. We also work with clients in Mumbai, Bangalore, Chennai, and internationally. Our digital services are available worldwide regardless of geography."
  },
  {
    question: "What is your typical web development process?",
    answer: "Our web development process has six stages: (1) Discovery and goal-setting, (2) UX research and sitemap planning, (3) UI/UX wireframing and design in Figma, (4) Full-stack development using Next.js with TypeScript, (5) QA testing and performance optimization including Core Web Vitals, and (6) Launch, SEO setup, and post-launch support."
  },
  {
    question: "How do you handle SEO for new websites?",
    answer: "We build SEO into the foundation of every website — not as an afterthought. This includes: semantic HTML structure, comprehensive Schema.org JSON-LD markup (Organization, Service, FAQPage, BreadcrumbList), optimized meta tags and Open Graph data, Core Web Vitals performance tuning, canonical URLs, XML sitemap generation, and robots.txt configuration. We also ensure AI crawlers (GPTBot, PerplexityBot) can index the site for AEO visibility."
  },
  {
    question: "How much does a website cost in India?",
    answer: "Website costs in India vary significantly by scope. A basic landing page starts from ₹25,000–₹50,000. A professional business website with custom design and CMS typically costs ₹80,000–₹2,50,000. A full-stack web application or e-commerce platform ranges from ₹2,50,000 to ₹10,00,000+. At Apex Visualtech, we provide transparent project-based pricing after a free discovery call to understand your specific requirements."
  },
  {
    question: "How long does a web development project take?",
    answer: "Project timelines depend on scope. A simple brochure website or landing page takes 2–3 weeks. A full business website with custom design takes 4–6 weeks. A complex web application or e-commerce platform typically takes 8–16 weeks. We provide a detailed timeline during our project proposal, and we adhere to it with structured milestone delivery."
  },
  {
    question: "Do you offer drone videography in Delhi NCR?",
    answer: "Yes. Apex Visualtech provides DGCA-compliant drone videography and aerial cinematography across Delhi, Noida, Gurugram, and the wider NCR region. We use cinema-grade drones with 4K and 6K capabilities. Our services cover real estate aerial photography, corporate events, brand films, weddings, and infrastructure documentation. We handle all permit applications required for aerial operations in the Delhi region."
  },
  {
    question: "What is included in a brand identity design package?",
    answer: "A complete brand identity package from Apex Visualtech includes: primary logo design (multiple variations — horizontal, stacked, icon-only), full color palette with HEX, RGB, and CMYK values, typography system selection and usage guidelines, brand voice guidelines, and a comprehensive brand guidelines PDF. We also offer extended packages that include business card design, social media templates, letterhead, and packaging mockups."
  },
  {
    question: "Can you redesign an existing website?",
    answer: "Absolutely. Website redesigns are a significant part of our work. Our redesign process begins with a full audit of your current site — performance, SEO, UX, and conversion issues — followed by a structured redesign that preserves your domain authority while dramatically improving design quality, performance scores, and search visibility. Existing content is migrated with proper 301 redirects to protect your organic rankings."
  },
  {
    question: "What is the difference between SEO and AEO?",
    answer: "SEO (Search Engine Optimization) focuses on ranking your website in traditional search engine results pages (Google, Bing) for specific keywords. AEO (Answer Engine Optimization) is the practice of optimizing your content so AI-powered answer engines — like ChatGPT, Google's AI Overviews, Perplexity, and Gemini — cite your website as an authoritative source. AEO requires structured data (FAQPage, HowTo schemas), direct question-answer content formats, and allowing AI crawlers to index your site. At Apex Visualtech, we implement both SEO and AEO for all our clients."
  }
]

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="py-24 border-t border-white/10 bg-[#0F172A]" id="faq">
      {/* FAQPage Schema for Search Engines and Answer Engines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map(faq => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
              }
            }))
          })
        }}
      />
      <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
        <div className="lg:col-span-5">
           <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-px bg-[#C8A96E]" />
            <span className="text-[#C8A96E] text-xs tracking-[0.3em]" style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>
              FAQ
            </span>
          </div>
          <h2 className="text-[clamp(3rem,6vw,5rem)] leading-none text-[#F8FAFC] mb-6" style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}>
            COMMON QUESTIONS
          </h2>
          <p className="text-[#94A3B8] text-lg leading-relaxed">
            Everything you need to know about our services, process, and how we deliver premium digital experiences.
          </p>
        </div>

        <div className="lg:col-span-7 flex flex-col pt-2 border-t border-white/10">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx
            return (
              <div key={idx} className="border-b border-white/10" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <button
                  type="button"
                  className="w-full flex items-center justify-between py-6 text-left group"
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                >
                  <h3 itemProp="name" className="text-xl md:text-2xl text-[#F8FAFC] group-hover:text-[#C8A96E] transition-colors pr-8 font-medium">
                    {faq.question}
                  </h3>
                  <div className="text-[#C8A96E] shrink-0">
                    {isOpen ? <Minus size={24} /> : <Plus size={24} />}
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                       <div className="pb-6 text-[#94A3B8] text-base md:text-lg leading-relaxed" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                         <div itemProp="text">
                           {faq.answer}
                         </div>
                       </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
